import React, { ReactElement, useState } from 'react'
import { Box, Typography } from '@mui/material'

import { LocationIcon } from '~assets/icons'
import { Button, InputLabel, LocationEntryModal, RemoveEntryButton } from '~components/common'
import { lightTheme } from '~createTheme'
import { Address0, formatAddress0 } from '~types'

type AddressInputTableProps = {
  setAddresses: React.Dispatch<React.SetStateAction<Address0[]>>
  addresses: Address0[]
}

const AddressInputTable = ({ setAddresses, addresses }: AddressInputTableProps): ReactElement => {
  const [showAddressInput, setShowAddressInput] = useState<boolean>(true)
  const [showLocationEntry, setShowLocationEntry] = useState<boolean>(false)

  const handleAddAddress = (address: Address0): void => {
    setAddresses((prev) => [...prev, address])
    setShowAddressInput(false)
  }

  const handleRemoveAddress = (address: Address0): void => {
    setAddresses((prev) => prev.filter((a) => a.id !== address.id))
  }

  const handleCancelAddAddress = (): void => {
    setShowAddressInput(false)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 2,
          mb: 2,
          borderBottom: '1px solid #ccc',
        }}
      >
        <InputLabel label='Addresses' isOptional icon={LocationIcon} />

        {/* Render addresses in a loop */}
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: showAddressInput ? 0 : 2 }}>
          {addresses.map((address) => (
            <Box
              key={address.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mr: 1,
              }}
            >
              <Box
                sx={{
                  backgroundColor: lightTheme.palette.grey[200],
                  borderRadius: '4px',
                  px: 2,
                  py: 1,
                }}
              >
                <Typography variant='body2'>{formatAddress0(address)}</Typography>
              </Box>
              <RemoveEntryButton
                row={address}
                onRemove={() => handleRemoveAddress(address)}
                showWarning={false}
                showConfirmation={false}
              />
            </Box>
          ))}
          {!showAddressInput && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <Box mr={8}>
                <Button
                  size='small'
                  variant='outlined'
                  color='secondary'
                  onClick={() => setShowAddressInput(true)}
                >
                  Add
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Address input for adding a new address */}
        {showAddressInput && (
          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                mb: 2,
              }}
            >
              <Box sx={{ width: '100%', minWidth: '300px' }}>
                {showLocationEntry && (
                  <LocationEntryModal
                    onSave={(address: Address0) => handleAddAddress(address)}
                    onClose={handleCancelAddAddress}
                  />
                )}
                {!showLocationEntry && (
                  <Box
                    onClick={() => setShowLocationEntry(true)}
                    tabIndex={0}
                    role='button'
                    aria-haspopup='dialog'
                    aria-expanded={showLocationEntry}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      height: '56px',
                      border: '1px solid',
                      borderRadius: 1,
                      px: 2,
                      cursor: 'text',
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        borderColor: 'text.primary',
                      },
                      '&:focus': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <Typography color='text.secondary'>Enter Location</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default AddressInputTable
